/* eslint-disable @typescript-eslint/no-type-alias */
/* eslint-disable object-curly-newline */
export type ROFRStatus =
  | 'both_detected'
  | 'needs_review'
  | 'no_rofr_detected'
  | 'rofo_detected'
  | 'rofr_detected';

export type ConfidenceLevel = 'high' | 'low' | 'medium';

// Option types that can be extracted from lease documents
export type OptionType = 'Expansion' | 'Extension' | 'Renewal' | 'ROFO' | 'ROFR' | 'Termination';

// Status of an option - can be changed by users
export type OptionStatus = 'Active' | 'Exercised' | 'Waived';

// Types of activities that can be logged
export type ActivityType = 'lease_expiration_updated' | 'Note Added' | 'note_added' | 'note_updated' | 'notice_period_updated' | 'rate_structure_updated' | 'sf_updated' | 'Status Changed' | 'status_changed' | 'Trigger Updated' | 'trigger_event_updated' | 'Unit Suite Updated' | 'unit_suite_updated' | 'Verified' | 'verified';

// Activity history entry (matches backend OptionActivityLogSerializer)
export interface ActivityEntry {
  id?: number;
  activity_type: string; // Backend uses snake_case
  type?: ActivityType; // Frontend legacy
  user_name?: string; // Backend field
  user?: string; // Legacy field
  timestamp: string;
  note?: string;
  old_status?: string;
  new_status?: string;
  details?: string; // e.g., "Active → Waived"
}

// User reference for verified by / status changed by
export interface UserReference {
  name: string;
  full_name?: string;
  email?: string;
  timestamp: string;
}

export interface ActionStatus {
  status: 'awaiting_trigger' | 'critical' | 'exercised' | 'in_window' | 'overdue' | 'safe' | 'unknown' | 'waived';
  label: string;
  notice_date?: string;
  days_remaining?: number;
  applies_to?: string;
  color_tier: 'critical' | 'muted' | 'neutral' | 'overdue' | 'safe' | 'warning';
}

export interface ROFRMonitorItem {
  id: number;
  lease_id: number;
  tenant_name: string;
  property_name: string | null;
  property_address: string | null;
  unit_suite: string | null;
  status: ROFRStatus;
  // New fields for flat option model
  option_type: OptionType | null;
  option_index: number;
  option_count: number;
  option_status: OptionStatus;
  status_note: string | null;
  status_changed_by: UserReference | null;
  applies_to: string;
  trigger_event: string;
  trigger_event_inferred: boolean;
  notice_period: string;
  confidence: ConfidenceLevel;
  // Updated verified_by to include user name
  verified_by: UserReference | null;
  last_verified: string | null; // Keep for backward compatibility
  is_confirmed: boolean;
  is_not_applicable: boolean;
  is_processing: boolean;
  lease_reference: string;
  clause_excerpt: string;
  key_conditions: string[];
  page_number: number | null;
  pdf_url: string;
  created: string;
  modified: string;
  // Activity history
  activity_history: ActivityEntry[];
  // Action Status fields
  action_status: ActionStatus;
  notice_date: string | null;
  lease_expiration_date: string | null;
  // Source document (amendment) info
  source_document_name: string | null;
  // SF / occupancy / rate structure columns
  tenant_sqft: number | null;
  pct_of_building: number | null;
  rate_structure: 'cpi' | 'fixed' | 'fmv' | null;
}

export interface ROFRListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ROFRMonitorItem[];
}

export interface ROFRDetailResponse {
  data: ROFRMonitorItem;
  headings: string[];
}

// Status display mapping
export const STATUS_DISPLAY: Record<ROFRStatus, string> = {
  rofr_detected: 'ROFR Detected',
  rofo_detected: 'ROFO Detected',
  both_detected: 'ROFR & ROFO',
  needs_review: 'Needs Review',
  no_rofr_detected: 'No ROFR Detected',
};

// Status color mapping (matching existing Strada patterns)
export const STATUS_COLORS: Record<ROFRStatus, { bg: string; text: string; border: string }> = {
  rofr_detected: { bg: '#FEE2E2', text: '#B91C1C', border: '#FECACA' },
  rofo_detected: { bg: '#FEF3C7', text: '#B45309', border: '#FDE68A' },
  both_detected: { bg: '#FCE7F3', text: '#BE185D', border: '#FBCFE8' },
  needs_review: { bg: '#DBEAFE', text: '#1D4ED8', border: '#BFDBFE' },
  no_rofr_detected: { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' },
};

// Confidence display mapping
export const CONFIDENCE_CONFIG: Record<ConfidenceLevel, { label: string; width: string; color: string }> = {
  high: { label: 'High', width: '100%', color: '#00CFA1' },
  medium: { label: 'Medium', width: '66%', color: '#F59E0B' },
  low: { label: 'Low', width: '33%', color: '#EF4444' },
};

// All status options for filters
export const ALL_STATUSES: ROFRStatus[] = [
  'rofr_detected',
  'rofo_detected',
  'both_detected',
  'needs_review',
  'no_rofr_detected',
];

// All option types for filters
export const ALL_OPTION_TYPES: OptionType[] = [
  'ROFR',
  'ROFO',
  'Expansion',
  'Renewal',
  'Extension',
  'Termination',
];

// All option statuses for filters
export const ALL_OPTION_STATUSES: OptionStatus[] = [
  'Active',
  'Waived',
  'Exercised',
];

// Option type display configuration
export const OPTION_TYPE_CONFIG: Record<OptionType, { label: string; color: string; bgColor: string; borderColor: string }> = {
  ROFR: { label: 'ROFR', color: '#B91C1C', bgColor: '#FEE2E2', borderColor: '#FECACA' },
  ROFO: { label: 'ROFO', color: '#B45309', bgColor: '#FEF3C7', borderColor: '#FDE68A' },
  Expansion: { label: 'Expansion', color: '#1D4ED8', bgColor: '#DBEAFE', borderColor: '#BFDBFE' },
  Renewal: { label: 'Renewal', color: '#047857', bgColor: '#D1FAE5', borderColor: '#A7F3D0' },
  Extension: { label: 'Extension', color: '#7C3AED', bgColor: '#EDE9FE', borderColor: '#DDD6FE' },
  Termination: { label: 'Termination', color: '#EA580C', bgColor: '#FFEDD5', borderColor: '#FED7AA' },
};

// Option status display configuration
export const OPTION_STATUS_CONFIG: Record<OptionStatus, { label: string; color: string; bgColor: string; borderColor: string }> = {
  Active: { label: 'Active', color: '#00CFA1', bgColor: 'rgba(0, 207, 161, 0.1)', borderColor: 'rgba(0, 207, 161, 0.3)' },
  Waived: { label: 'Waived', color: '#6B7280', bgColor: '#F3F4F6', borderColor: '#E5E7EB' },
  Exercised: { label: 'Exercised', color: '#6B7280', bgColor: '#F3F4F6', borderColor: '#E5E7EB' },
};

export const ACTION_STATUS_COLORS: Record<string, { dot: string; text: string }> = {
  safe: { dot: '#D4A017', text: '#D4A017' },
  warning: { dot: '#E67E22', text: '#E67E22' },
  critical: { dot: '#C0392B', text: '#C0392B' },
  overdue: { dot: '#8B0000', text: '#8B0000' },
  neutral: { dot: '#9CA3AF', text: '#6B7280' },
  muted: { dot: '#D1D5DB', text: '#9CA3AF' },
};

// Feature flags for future expansion
export const FEATURE_FLAGS = {
  SHOW_RENEWAL_OPTIONS: process.env.REACT_APP_SHOW_RENEWAL_OPTIONS === 'true',
  SHOW_TERMINATION_OPTIONS: process.env.REACT_APP_SHOW_TERMINATION_OPTIONS === 'true',
};

export interface OptionDetail {
  id: string;
  type: OptionType;
  practicalEffect: string;
  appliesTo: string;
  triggerEvent: string;
  noticePeriod: string;
  actionByDate: string | null;
  keyConditions?: string[];
  confidence: ConfidenceLevel;
  leaseExcerpt: string;
  sectionCitation: string;
  // Renewal-specific fields
  renewalTerm?: string;
  rentBasis?: string;
  // Termination-specific fields
  terminationFee?: string;
}
